import {
  Field,
  InfoBox,
  RadioCardGroup,
  Stack,
  Text,
  Textarea,
} from "components/ui";
import {
  NOTIFICATION_RECIPIENT_POLICIES,
  NotificationChannel,
  NotificationRecipientPolicy,
} from "services/notificationService";

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface RecipientPolicyState {
  policy: NotificationRecipientPolicy;
  /** One address or `@domain` per line, as typed. */
  allowedRecipients: string;
}

export const recipientPolicyFrom = (
  channel: NotificationChannel | null,
): RecipientPolicyState => ({
  policy: channel?.recipientPolicy ?? "SignedInUser",
  allowedRecipients: (channel?.allowedRecipients ?? []).join("\n"),
});

const allowedRecipientList = (text: string) =>
  text
    .split(/[\n,;]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

export const validateRecipientPolicy = (
  state: RecipientPolicyState,
  t: Translate,
): string | null =>
  state.policy === "AllowList" &&
  allowedRecipientList(state.allowedRecipients).length === 0
    ? t("app.notifications.channel.recipients.allowListEmpty")
    : null;

export const recipientPolicyRequest = (state: RecipientPolicyState) => ({
  recipientPolicy: state.policy,
  allowedRecipients: allowedRecipientList(state.allowedRecipients),
});

export const recipientPolicySummary = (
  channel: NotificationChannel,
  t: Translate,
) => {
  const policy = channel.recipientPolicy ?? "SignedInUser";
  return t(`app.notifications.channel.recipients.summary.${policy}`, {
    count: channel.allowedRecipients?.length ?? 0,
  });
};

interface RecipientPolicyFieldsProps {
  value: RecipientPolicyState;
  onChange: (next: RecipientPolicyState) => void;
  t: Translate;
}

/**
 * Who the AI's send_email tool may deliver to on an SMTP channel. The model
 * writes the recipient, so by default the server ignores it and sends to the
 * signed-in customer's own address; an allow-list or "anyone" are opt-ins.
 */
export const RecipientPolicyFields = ({
  value,
  onChange,
  t,
}: RecipientPolicyFieldsProps) => (
  <Stack gap={3}>
    <Field
      label={t("app.notifications.channel.recipients.label")}
      helperText={t("app.notifications.channel.recipients.help")}
    >
      <RadioCardGroup<NotificationRecipientPolicy>
        value={value.policy}
        onChange={(policy) => onChange({ ...value, policy })}
        minWidth={170}
        options={NOTIFICATION_RECIPIENT_POLICIES.map((policy) => ({
          id: policy,
          label: t(`app.notifications.channel.recipients.${policy}.label`),
          sub: t(`app.notifications.channel.recipients.${policy}.sub`),
        }))}
      />
    </Field>
    {value.policy === "AllowList" && (
      <Field
        label={t("app.notifications.channel.recipients.allowListLabel")}
        helperText={t("app.notifications.channel.recipients.allowListHelp")}
        required
      >
        <Textarea
          rows={4}
          value={value.allowedRecipients}
          placeholder={"ops@acme.com\n@acme.com"}
          onChange={(e) =>
            onChange({ ...value, allowedRecipients: e.target.value })
          }
        />
      </Field>
    )}
    {value.policy === "Any" && (
      <InfoBox variant="warning">
        <Text fontSize="sm">
          {t("app.notifications.channel.recipients.anyWarning")}
        </Text>
      </InfoBox>
    )}
  </Stack>
);
